import React, { useEffect, useState } from "react";
import Announcements from "./announcements";
import { getAnnouncements } from "./api";
import QualityHoldWidget from "./widgets/qualityHold";
import NewQuotesWidget from "./widgets/newQuotes";
import UrgentActionOrdersWidget from "./widgets/urgentActions";
import { getQualityHoldOrders } from "./widgets/qualityHold/api";
import { log } from "../Helper";
import { activeQuotesTableData } from "../ForwarderQuoting/api";
import { useUserInfo } from "../common/user-context";
import EmptyAnnouncements from "./emptyAnnouncements";
import Dashboard from "./dashboard";
import CardLoaderComponent from "../CardLoaderComponent";

const HomePage = () => {
  const { userData } = useUserInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [isQuotesLoading, setIsQuotesLoading] = useState(false);
  const [isAnnouncementLoading, setIsAnnouncementLoading] = useState(false);
  const [announcementData, setAnnouncementData] = useState([]);
  const [qualityHoldData, setQualityHoldData] = useState([]);
  const [newQuotesData, setNewQuotesData] = useState([]);
  const [isNewQuotesDataAvailable, setIsNewQuotesDataAvailable] =
    useState(false);
  const [isQualityHoldDataAvailable, setQualityHoldDataAvailable] =
    useState(false);
  const [pageSize, setPageSize] = useState(200);
  const [pageNumber, setPageNumber] = useState(0);
  const [isExternalFactoryUser, setIsExternalFactoryUser] = useState(false);

  const getAnnouncementsList = async () => {
    try {
      setIsAnnouncementLoading(true);
      const response = await getAnnouncements(userData);
      const sortDescAnn = [...response];
      sortDescAnn.sort(function (a, b) {
        return b.publishDate.localeCompare(a.publishDate, "en", { sensitivity: "base" });
      })
      setAnnouncementData(sortDescAnn);
      if (sortDescAnn) {
        setIsAnnouncementLoading(false);
      }
    } catch (error) {
      log("Error occured when fetching announcements: ", error);
      setAnnouncementData([]);
      setIsAnnouncementLoading(false);
    }
  };

  const getQualityHoldOrdersList = async () => {
    try {
      setIsLoading(true);
      const response = await getQualityHoldOrders(userData);
      if (response) {
        setQualityHoldDataAvailable(true);
        setQualityHoldData(response);
      }
    } catch (error) {
      log("Error occured when fetching data: ", error);
      setQualityHoldData([]);
    }
    setIsLoading(false);
  };

  const getNewQuotes = async () => {
    try {
      setIsQuotesLoading(true);
      const response = await activeQuotesTableData(
        pageNumber,
        pageSize,
        userData
      );
      if (response) {
        setIsNewQuotesDataAvailable(true);
        setNewQuotesData(response);
      }
    } catch (error) {
      log("Error occured when fetching data: ", error);
      setNewQuotesData([]);
    }
    setIsQuotesLoading(false);
  };

  useEffect(() => {
    setIsQuotesLoading(true);
    setIsAnnouncementLoading(true);
    if (userData.length > 0 || userData.userId) {
      setIsQuotesLoading(false);
      setIsAnnouncementLoading(false);
      // Check if the user is a forwarder and external factory user then treat that as a forwarder for homepage display
      if (userData.forwarder && userData.externalFactoryUser) {
        setIsExternalFactoryUser(false);
      } else {
        setIsExternalFactoryUser(userData.externalFactoryUser);
      }
      getAnnouncementsList();
      // display widgets for all cases except if it's only external factory user
      if (!userData.externalFactoryUser || userData.forwarder) {
        getQualityHoldOrdersList();
        getNewQuotes();
      }
    }
  }, [userData]);

  return ( 
    <div className="bg-gray px-4 py-1 justify-start items-start gap-2 flex h-screen">
      <div className="w-3/4 grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
        {!isExternalFactoryUser ? (
          <div className="self-stretch flex-col gap-2 inline-flex">
            {/* First Row - Important Orders Widget and Dashboard (Carrier Performance Report) */}
            <div className="self-stretch justify-around gap-2 inline-flex">
              <div className="w-full px-3 pt-2 pb-3 bg-white rounded-xl border border-stone-300 flex-col justify-around inline-flex">
                <div className="p-0">
                  <UrgentActionOrdersWidget />
                </div>
              </div>
              <div className="w-full px-3 pt-2 pb-3 bg-white rounded-xl border border-stone-300 flex-col justify-around inline-flex">
                <div className="p-0">
                  <Dashboard />
                </div>
              </div>
            </div>
            
            {/* Second Row - NewQuotes and QualityHold widgets */}
            <div className="self-stretch justify-around gap-2 inline-flex">
              <div className="w-full px-3 pt-2 pb-3 bg-white rounded-xl border border-stone-300 flex-col justify-around inline-flex">
                <div className="p-0">
                  <NewQuotesWidget
                    tableData={newQuotesData}
                    isLoading={isQuotesLoading}
                    isNewQuotesDataAvailable={isNewQuotesDataAvailable}
                  />
                </div>
              </div>
              <div className="w-full h-3/4 px-3 pt-2 pb-3 bg-white rounded-xl border border-stone-300 flex-col justify-around inline-flex">
                <div className="p-0">
                  <QualityHoldWidget
                    tableData={qualityHoldData}
                    isLoading={isLoading}
                    isQualityHoldDataAvailable={isQualityHoldDataAvailable}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="w-[340px] flex-col justify-start items-start gap-2 h-screen inline-flex">
        <div className="justify-start items-start gap-2 inline-flex">
          <div className="text-zinc-900 text-2xl font-semibold font-['Open Sans'] leading-loose">
            Announcements
          </div>
          <div className="text-zinc-900 text-2xl font-semibold font-['Open Sans'] leading-loose">
            ({announcementData.length})
          </div>
        </div>
        {isAnnouncementLoading && (
          <CardLoaderComponent
            desc="announcement"
            subtitle="This will take a couple seconds"
          />
        )}
        {!isAnnouncementLoading && (
          <div className="overflow-auto flex-col justify-start items-start gap-2 inline-flex">
            <div className="flex-col justify-start items-start gap-2 inline-flex">
              {!announcementData.length == 0 ? (
                announcementData.map((ann) => (
                  <div>
                    <Announcements
                      header={ann.header}
                      message={ann.message}
                      publishDate={ann.publishDate}
                    />
                  </div>
                ))
              ) : (
                <div>
                  <EmptyAnnouncements />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
